import { useEffect, useReducer, useState } from "react";
import { ImageUpload } from "../ui/ImageUpload";
import SampleImage from "./SampleImage";
import { useSampleEdit } from "../../api";
import { Button, Alert } from "react-bootstrap";
import {
  createImageState,
  getCancelPayload,
  getSavePayload,
  hasImageChanges,
  sampleImageStateReducer,
} from "./sampleImageState";

const imageDisclaimer = (
  <div className='fst-italic text-muted mt-3' style={{ fontSize: "0.7rem" }}>
    <strong>Photo uploads are for sample documentation only.</strong> Please
    upload clear photos of the substance or packaging being tested. Please do
    not upload photos containing personal information, faces, or any
    sensitive/unrelated content.
  </div>
);

export default function SampleImageManager({
  id,
  sampleId,
  sampleImages = [],
  onDirtyChange,
}) {
  const [imageState, dispatch] = useReducer(
    sampleImageStateReducer,
    undefined,
    createImageState,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const { mutateAsync, isLoading: editLoading } = useSampleEdit(id);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setError("");
  }, [id]);

  useEffect(() => {
    onDirtyChange?.(imageState.isEditing && hasImageChanges(imageState));
  }, [imageState, onDirtyChange]);

  const handleImagesChange = (imageData) => {
    dispatch({ type: "SET_IMAGES", images: imageData || [] });
  };

  const handleEditClick = () => {
    dispatch({ type: "START_EDIT", sampleImages });
  };

  const handleSave = async () => {
    if (!hasImageChanges(imageState)) return;

    setIsSaving(true);
    try {
      await mutateAsync({
        id: id,
        data: {
          ...getSavePayload(imageState),
        },
      });
      dispatch({ type: "RESET" });
    } catch (error) {
      console.error("Error saving images:", error);
      // /edit-sample errors are envelopes now ({ error: { code, message } });
      // the plain-string body is the pre-migration shape.
      const data = error.response?.data;
      setError(
        typeof data === "string" && data
          ? data
          : data?.error?.message ||
              data?.message ||
              "Failed to save images. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    const cancelPayload = getCancelPayload(imageState);

    if (!cancelPayload) {
      dispatch({ type: "RESET" });
      setError("");
      return;
    }

    setIsSaving(true);
    try {
      await mutateAsync({
        id: id,
        data: cancelPayload,
      });

      dispatch({ type: "RESET" });
      setError("");
    } catch (error) {
      console.error("Error canceling image changes:", error);
      setError("Failed to discard images. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!imageState.isEditing && (!sampleImages || sampleImages.length === 0)) {
    return (
      <div className='my-2 full-height'>
        <div className='card full-height'>
          <div className='card-body'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h5 className='card-title text-primary mb-0'>Sample Photos</h5>
              <Button variant='primary' size='sm' onClick={handleEditClick}>
                <i className='bi bi-plus-circle pe-1'></i>
                Add Photos
              </Button>
            </div>
            <div className='alert alert-info d-flex align-items-center' role='alert'>
              <i className='bi bi-info-circle pe-2'></i>
              No photos available for this sample.
            </div>
            {imageDisclaimer}
          </div>
        </div>
      </div>
    );
  }

  if (imageState.isEditing) {
    return (
      <div className='my-2 full-height'>
        <div className='card full-height'>
          <div className='card-body'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h5 className='card-title text-primary mb-0'>
                {sampleImages && sampleImages.length > 0
                  ? "Update Sample Photos"
                  : "Add Sample Photos"}
              </h5>
            </div>
            {error && (
              <Alert
                variant='danger'
                className='mb-0 '
                onClose={() => setError("")}
                dismissible
              >
                {error}
              </Alert>
            )}
            <ImageUpload
              key={id || sampleId}
              sampleId={sampleId}
              onImagesChange={handleImagesChange}
              images={imageState.currentImages}
              maxImages={3}
            />
            <div className='d-flex gap-2 mt-3'>
              <Button
                variant='primary'
                onClick={handleSave}
                disabled={
                  !imageState.currentImages ||
                  isSaving ||
                  editLoading ||
                  !hasImageChanges(imageState)
                }
              >
                {isSaving || editLoading ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className='bi bi-save pe-1'></i>
                    Save Photos
                  </>
                )}
              </Button>
              <Button
                variant='secondary'
                onClick={handleCancel}
                disabled={isSaving || editLoading}
              >
                Cancel
              </Button>
            </div>
            {imageDisclaimer}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='my-2 full-height'>
      <div className='card full-height'>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h5 className='card-title text-primary mb-0'>
              Sample Photos ({sampleImages.length}/3)
            </h5>

            <Button
              variant='outline-primary'
              size='sm'
              onClick={handleEditClick}
            >
              <i className='bi bi-pencil pe-1'></i>
              Manage Photos
            </Button>
          </div>
          <SampleImage sampleImages={sampleImages} />
          {imageDisclaimer}
        </div>
      </div>
    </div>
  );
}
